import React, { useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';

export const RankingSection = () => {
  const [progress, setProgress] = useState(0);
  const targetProgress = 75;

  const ranks = [
    { id: 'peao', name: 'Peão', color: '#CD7F32', points: '0-999' },
    { id: 'jogador', name: 'Jogador', color: '#C0C0C0', points: '1000-1999' },
    { id: 'truqueiro', name: 'Truqueiro', color: '#FFD700', points: '2000-2999' },
    { id: 'blefador', name: 'Blefador', color: '#00CED1', points: '3000-3999' },
    { id: 'mestre-da-mesa', name: 'Mestre da Mesa', color: '#B9F2FF', points: '4000-4999' },
    { id: 'lenda-do-truco', name: 'Lenda do Truco', color: '#FF00FF', points: '5000+' }
  ];

  const currentRank = ranks[3]; // Blefador
  const nextRank = ranks[4]; // Mestre da Mesa

  useEffect(() => {
    const timer = setTimeout(() => {
      setProgress(targetProgress);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-[#0A0A0F] to-[#05050A] relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-primary/10 rounded-full blur-[150px]"></div>
      </div>

      <div className="relative z-10 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-8 lg:mb-12">
          <p className="overline mb-2">Progressão</p>
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black font-archive text-white mb-3">
            Sistema de Ranking
          </h2>
          <p className="text-sm sm:text-base text-gray-400 max-w-md mx-auto">
            Evolua, suba de ranking e mostre sua habilidade
          </p>
        </div>

        {/* Profile Card - Mobile First Design */}
        <div className="profile-glow game-card rounded-3xl p-5 sm:p-6 lg:p-8 mb-8" data-testid="profile-card">
          {/* Top Section: Avatar + Info */}
          <div className="flex items-start gap-4 mb-6">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden border-2 border-purple-primary/50">
                <img 
                  src="https://images.unsplash.com/photo-1772371272218-d39eca1efdeb?crop=entropy&cs=srgb&fm=jpg&w=200"
                  alt="Avatar do jogador"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Level Badge */}
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-purple-primary rounded-lg flex items-center justify-center text-white text-xs font-bold border-2 border-[#0A0A0F]">
                42
              </div>
            </div>

            {/* Player Info */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg sm:text-xl font-bold text-white truncate">TrucoMaster_BR</h3>
              <p className="text-sm text-gray-400 mb-2">ID: #847291</p>
              
              {/* Current Rank */}
              <div className="flex items-center gap-2">
                <div 
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${currentRank.color}, ${currentRank.color}80)` }}
                >
                  <span className="text-xs font-bold text-white/90">P</span>
                </div>
                <span className="font-bold" style={{ color: currentRank.color }}>{currentRank.name}</span>
              </div>
            </div>

            {/* Stats */}
            <div className="text-right hidden sm:block">
              <p className="text-2xl font-black text-white">3,450</p>
              <p className="text-xs text-gray-400">pontos</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-400">Progresso para {nextRank.name}</span>
              <span className="text-sm font-bold" style={{ color: nextRank.color }}>
                {progress}%
              </span>
            </div>
            <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-1000 ease-out"
                style={{ 
                  width: `${progress}%`,
                  background: `linear-gradient(90deg, ${currentRank.color}, ${nextRank.color})`
                }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-xs text-gray-500">3,450 pts</span>
              <span className="text-xs text-gray-500">4,000 pts</span>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            <div className="bg-black/30 rounded-xl p-3 text-center">
              <p className="text-xl sm:text-2xl font-black text-green-primary">156</p>
              <p className="text-[10px] sm:text-xs text-gray-400">Vitórias</p>
            </div>
            <div className="bg-black/30 rounded-xl p-3 text-center">
              <p className="text-xl sm:text-2xl font-black text-white">68%</p>
              <p className="text-[10px] sm:text-xs text-gray-400">Win Rate</p>
            </div>
            <div className="bg-black/30 rounded-xl p-3 text-center">
              <p className="text-xl sm:text-2xl font-black text-purple-light">12</p>
              <p className="text-[10px] sm:text-xs text-gray-400">Sequência</p>
            </div>
          </div>
        </div>

        {/* Rank Progression Timeline */}
        <div className="mb-6">
          <h4 className="text-sm font-bold text-gray-400 mb-4 text-center">Rankings</h4>
          
          {/* Mobile: Horizontal Scroll */}
          <div className="snap-carousel hidden-scrollbar -mx-4 pb-2">
            <div className="flex items-center gap-2 px-4">
              {ranks.map((rank, index) => {
                const isCurrentOrPast = index <= 3;
                const isCurrent = index === 3;
                
                return (
                  <div key={rank.id} className="flex items-center">
                    {/* Rank Node */}
                    <div 
                      className={`flex-shrink-0 flex flex-col items-center ${isCurrent ? 'scale-110' : ''}`}
                      data-testid={`rank-node-${rank.id}`}
                    >
                      <div 
                        className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center transition-all ${
                          isCurrent ? 'ring-2 ring-white ring-offset-2 ring-offset-[#0A0A0F]' : ''
                        } ${isCurrentOrPast ? '' : 'opacity-40'}`}
                        style={{ 
                          background: isCurrentOrPast 
                            ? `linear-gradient(135deg, ${rank.color}, ${rank.color}80)` 
                            : '#1a1a2e'
                        }}
                      >
                        <span className="text-sm font-bold text-white/90">
                          {rank.name.charAt(0)}
                        </span>
                      </div>
                      <span 
                        className={`text-[10px] sm:text-xs mt-1 font-medium whitespace-nowrap ${
                          isCurrent ? 'text-white' : isCurrentOrPast ? 'text-gray-400' : 'text-gray-600'
                        }`}
                      >
                        {rank.name}
                      </span>
                    </div>
                    
                    {/* Connector */}
                    {index < ranks.length - 1 && (
                      <div className="flex-shrink-0 mx-1">
                        <ChevronRight 
                          size={16} 
                          className={isCurrentOrPast && index < 3 ? 'text-green-primary' : 'text-gray-700'} 
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button 
            data-testid="ranking-cta"
            className="btn-neon px-8 py-4 rounded-xl font-bold"
          >
            Começar a Subir de Ranking
          </button>
        </div>
      </div>
    </section>
  );
};
